import { BrokerCostType } from '../../../Enums/BrokerCostType';
import { hasFieldChangedValue } from '../../../Helpers/FormInputHelper';
import { formatMessage } from '../../../Localization/formatMessage';
import Messages from '../../../Localization/Messages';
import { bcrModalModeEnum } from '../CommissionPage';

const staticRateForPersonalCommission = 10;
const bcrTypes = [
  BrokerCostType.Casco,
  BrokerCostType.Liability,
  BrokerCostType.Person,
  BrokerCostType.Property,
  BrokerCostType.Transport,
];

export const getModalTitleText = (modalMode, customerNumber) => {
  return formatMessage(Messages[`commissionPage_modalTitle${modalMode}`], { customerNumber });
};

export const getRatesWhenUsePersonalCommissionChange = (rates, newUsePersonalCommissionValue) => {
  const newRates = rates.filter(rate => rate.rateType !== BrokerCostType.PersonalCommission);
  newRates.push({
    rateType: BrokerCostType.PersonalCommission,
    rate: newUsePersonalCommissionValue ? staticRateForPersonalCommission : 0,
  });
  return newRates;
};

export const getRatesWhenUseMotorCommissionChange = (rates, newUseMotorCommissionValue) => {
  if (newUseMotorCommissionValue) {
    return rates;
  }

  const newRates = rates.filter(rate => rate.rateType !== BrokerCostType.MotorCommission);
  newRates.push({
    rateType: BrokerCostType.MotorCommission,
    rate: 0,
  });
  return newRates;
};

export const getRatesWhenUseBrokerCostChange = (rates, newUseBrokerCostValue) => {
  if (newUseBrokerCostValue) return rates;

  const newRates = [
    {
      rateType: BrokerCostType.MotorCommission,
      rate: getRate(rates, BrokerCostType.MotorCommission),
    },
    {
      rateType: BrokerCostType.PersonalCommission,
      rate: getRate(rates, BrokerCostType.PersonalCommission),
    },
  ];

  bcrTypes.forEach(rateType => {
    newRates.push({ rateType, rate: 0 });
  });

  return newRates;
};

export const getInputFieldsProperties = (rateType, editedBcr, setEditedRate) => {
  return {
    label: formatMessage(Messages[`commissionPage_rateType${rateType}`]),
    value: getRate(editedBcr.rates, rateType),
    onChange: event => {
      const newRates = editedBcr.rates?.filter(x => x.rateType !== rateType) ?? [];
      setEditedRate({
        ...editedBcr,
        rates: [...newRates, { rateType, rate: event.target.value }],
      });
    },
  };
};

export const validateFields = (editedBcr, modalMode, existingCustomers) => {
  const errors = [];
  if (modalMode === bcrModalModeEnum.editCustomerRate || modalMode === bcrModalModeEnum.newCustomerRate) {
    if (!editedBcr.customerName) {
      errors.push({
        inputId: 'customerSearch',
        message: formatMessage(Messages.commissionPage_customerNotSelected),
      });
    }
  }

  if (modalMode === bcrModalModeEnum.newCustomerRate && editedBcr.customerNumber) {
    const result = existingCustomers.findIndex(customer => customer.customerNumber === editedBcr.customerNumber);
    if (result !== -1) {
      errors.push({
        inputId: 'customerSearch',
        message: formatMessage(Messages.commissionPage_customerExistsForAgent),
      });
    }
  }

  return errors;
};

export const haveFieldsChanged = (editedBcr, bcr, modalMode) => {
  if (modalMode === bcrModalModeEnum.createDefaultRate || modalMode === bcrModalModeEnum.newCustomerRate) {
    return true;
  }

  if (hasFieldChangedValue(editedBcr.inheritCommissionHandling, bcr.inheritCommissionHandling)) {
    return true;
  }

  if (hasFieldChangedValue(editedBcr.usePersonalCommission, bcr.usePersonalCommission)) {
    return true;
  }

  if (hasFieldChangedValue(editedBcr.useMotorCommission, bcr.useMotorCommission)) {
    return true;
  } else if (
    editedBcr.useMotorCommission &&
    hasRateChangedValue(editedBcr.rates, bcr.rates, BrokerCostType.MotorCommission)
  ) {
    return true;
  }

  if (hasFieldChangedValue(editedBcr.useBrokerCost, bcr.useBrokerCost)) {
    return true;
  } else if (editedBcr.useBrokerCost) {
    for (const rateType of bcrTypes) {
      if (hasRateChangedValue(editedBcr.rates, bcr.rates, rateType)) {
        return true;
      }
    }
  }

  return false;
};

export const getRatesBasedOnModalMode = (bcr, modalMode) => {
  if (modalMode === bcrModalModeEnum.createDefaultRate || modalMode === bcrModalModeEnum.newCustomerRate) {
    return {
      ...bcr,
      rates: [
        { rateType: BrokerCostType.Property, rate: 0 },
        { rateType: BrokerCostType.Liability, rate: 0 },
        { rateType: BrokerCostType.Person, rate: 0 },
        { rateType: BrokerCostType.Transport, rate: 0 },
        { rateType: BrokerCostType.Casco, rate: 0 },
        { rateType: BrokerCostType.MotorCommission, rate: 0 },
        { rateType: BrokerCostType.PersonalCommission, rate: 0 },
      ],
    };
  }

  return bcr;
};

const getRate = (rates, rateType) => {
  return rates?.find(x => x.rateType === rateType)?.rate;
};

const hasRateChangedValue = (editedRates, initialRates, rateType) => {
  return hasFieldChangedValue(getRate(editedRates, rateType), getRate(initialRates, rateType));
};
